import { info } from '@ms/yammer-console-logging';
import { isGraphqlApiError } from '@ms/yammer-data/dist/api';
import { getExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import type {
  PublishGroupMessageFulfilledAction,
  PublishGroupMessagePendingAction,
  PublishGroupMessageRejectedAction,
} from '@ms/yammer-data/dist/domains/publishers/actions.types';
import type { PostShownScenarioHandle } from '@ms/yammer-scenario-telemetry';
import { recordScenarioCall, startPostShownScenario } from '@ms/yammer-scenario-telemetry';
import type { AnyAction, Middleware } from 'redux';

import type { YammerWebState } from '../../../state/types';

/**
 * The BE operation name for web's primary feed publish. Matches the call the `post_shown`
 * scenario is built around, so the traceability ledger reports it under mobile's/BE's op name.
 */
const publishGroupMessageOperationName = 'PublishGroupMessageClients';

interface InFlightPostShown {
  readonly handle: PostShownScenarioHandle;
  readonly startedAt: number;
}

const isPendingAction = (action: AnyAction): action is PublishGroupMessagePendingAction =>
  action.type === 'PUBLISHER.PUBLISH_GROUP_MESSAGE_PENDING';

const isFulfilledAction = (action: AnyAction): action is PublishGroupMessageFulfilledAction =>
  action.type === 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED';

const isRejectedAction = (action: AnyAction): action is PublishGroupMessageRejectedAction =>
  action.type === 'PUBLISHER.PUBLISH_GROUP_MESSAGE_REJECTED';

/**
 * Walking-skeleton wiring for the `post_shown` interaction scenario on web's primary feed.
 *
 * Observes the Redux group-message publish lifecycle and drives a `post_shown` scenario per
 * publisher: `PENDING` starts it, `FULFILLED` hands it the server-confirmed thread's
 * `telemetryId` so it stops as `ok` when that thread paints in the feed (the content-shown
 * signal is emitted by `ThreadStarter`), and `REJECTED` stops it as `failure`. Draft saves are
 * ignored. The whole path is gated on `ScenarioBasedQosTelemetry`, checked at submit time, so it
 * is inert when the experiment is off.
 *
 * The scenario primitive is data-layer agnostic; this middleware is the web-feed adapter that
 * translates Redux publish actions into its lifecycle. `onSettled` mirrors the outcome to the
 * console so the emitted telemetry can be observed on a private deploy without the lean
 * telemetry module taking a logging dependency.
 *
 * It also records the publish call into the scenario's traceability call-ledger
 * (`recordScenarioCall`): the group-message publish is the primary backend call that composes
 * `post_shown`, so on `FULFILLED`/`REJECTED` its operation name, outcome and wall-clock
 * duration are attributed to the scenario. On the failure path it also captures the backend
 * `X-Request-Id` (carried on `GraphqlApiError`) so a failed post can be traced to the exact
 * downstream request; the ok path surfaces the request id via the `api_fetch` telemetry event
 * instead. The façade emits the ledger only when the scenario itself ends non-ok, letting a
 * failed/timed-out post_shown be told apart (publish API failed vs. publish succeeded but the
 * node never painted).
 */
export const createPostShownScenarioMiddleware = (): Middleware<Record<string, never>, YammerWebState> => {
  const inFlightByPublisher = new Map<string, InFlightPostShown>();

  return (store) => (next) => (action: AnyAction) => {
    if (isPendingAction(action)) {
      const { publisherId, isDraft } = action.payload;
      const isEnabled = getExperimentTreatment(store.getState(), 'ScenarioBasedQosTelemetry');

      if (isEnabled && !isDraft && !inFlightByPublisher.has(publisherId)) {
        info('[post_shown] scenario started', { publisherId });
        inFlightByPublisher.set(publisherId, {
          handle: startPostShownScenario({
            source: 'engage_feed',
            onSettled: (status) => {
              info('[post_shown] scenario settled', { publisherId, status });
            },
          }),
          startedAt: Date.now(),
        });
      }
    } else if (isFulfilledAction(action)) {
      const { publisherId, threadId, entities } = action.payload;
      const inFlight = inFlightByPublisher.get(publisherId);

      if (inFlight) {
        const { handle, startedAt } = inFlight;
        inFlightByPublisher.delete(publisherId);
        recordScenarioCall(handle.scenarioId, {
          operationName: publishGroupMessageOperationName,
          status: 'ok',
          durationMs: Date.now() - startedAt,
        });
        const contentKey = entities.threads[threadId].telemetryId;
        info('[post_shown] publish confirmed, awaiting paint', { publisherId, contentKey });
        handle.publishSucceeded(contentKey);
      }
    } else if (isRejectedAction(action)) {
      const { publisherId, error, errorCode } = action.payload;
      const inFlight = inFlightByPublisher.get(publisherId);

      if (inFlight) {
        const { handle, startedAt } = inFlight;
        inFlightByPublisher.delete(publisherId);
        const requestId = isGraphqlApiError(error) ? error.requestId : undefined;
        recordScenarioCall(handle.scenarioId, {
          operationName: publishGroupMessageOperationName,
          status: 'failure',
          durationMs: Date.now() - startedAt,
          ...(errorCode ? { errorReason: errorCode } : {}),
          ...(requestId ? { requestId } : {}),
        });
        info('[post_shown] publish failed', { publisherId, errorCode, requestId });
        handle.publishFailed(new Error(errorCode ?? 'publish failed'));
      }
    }

    return next(action);
  };
};
